// src/services/termsService.js
// Gerenciamento de Termos de Uso com versionamento — Firebase/Firestore
// Adaptado da implementação da Intranet RP Métodos (routes/termos.js)

import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';

// Versão padrão usada quando nenhuma configuração foi publicada ainda.
// Mantém compatibilidade com usuários que já aceitaram antes do versionamento.
export const TERMS_DEFAULT_VERSION = '2026-05';

/**
 * Assina o documento `config/terms` em tempo real.
 * Chama `callback` com os dados do documento sempre que houver mudança.
 * Se o documento não existir, chama com `null`.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} configPath - Caminho da coleção config (ex: artifacts/.../public/data/config)
 * @param {function} callback - (config: { version: string, updatedAt: Timestamp } | null) => void
 * @returns {function} unsubscribe
 */
export function subscribeTermsConfig(db, configPath, callback) {
  const ref = doc(db, configPath, 'terms');
  return onSnapshot(
    ref,
    (snap) => {
      callback(snap.exists() ? snap.data() : null);
    },
    (err) => {
      console.warn('[termsService] Erro ao ler config/terms:', err.message);
      callback(null);
    }
  );
}

/**
 * Publica uma nova versão dos termos no Firestore.
 * A versão é a data de hoje no formato "YYYY-MM-DD".
 * Todos os usuários com `termsVersion` diferente precisarão aceitar novamente.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} configPath - Caminho da coleção config
 * @returns {Promise<string>} versão publicada (ex: "2026-05-15")
 */
export async function publishNewTermsVersion(db, configPath) {
  const version = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
  const ref = doc(db, configPath, 'terms');
  await setDoc(ref, { version, updatedAt: serverTimestamp() });
  return version;
}

/**
 * Gera e abre o comprovante de aceite dos termos em nova janela do browser.
 * O usuário pode imprimir ou salvar como PDF.
 * Design inspirado no comprovante da Intranet RP Métodos.
 *
 * @param {object} user - Dados do usuário ({ name, email, role, termsAcceptedAt, termsVersion })
 * @param {string} termsVersion - Versão atual dos termos (fallback)
 */
export function generateTermsPDF(user, termsVersion) {
  const AZUL = '#1B2B6B';
  const VERM = '#C0181C';

  // Converte Firestore Timestamp ou string ISO para Date
  const dataAceite = user.termsAcceptedAt
    ? (typeof user.termsAcceptedAt.toDate === 'function'
        ? user.termsAcceptedAt.toDate()
        : new Date(user.termsAcceptedAt))
    : null;

  const formatDate = (d) =>
    d
      ? d.toLocaleString('pt-BR', {
          timeZone: 'America/Sao_Paulo',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      : '—';

  const roleLabel =
    Number(user.role) === 3 ? 'Administrador'
    : Number(user.role) === 2 ? 'Gestor'
    : 'Funcionário';

  const versaoAceita = user.termsVersion || '(versão anterior ao versionamento)';
  const geradoEm = formatDate(new Date());

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Comprovante de Aceite — ${user.name || user.email || 'Usuário'}</title>
  <style>
    @page { size: A4; margin: 2cm; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #374151;
      margin: 0;
      padding: 0;
      background: white;
    }
    .header {
      background: ${AZUL};
      color: white;
      padding: 28px 32px;
      text-align: center;
    }
    .header h1 { margin: 0 0 6px; font-size: 18px; font-weight: bold; }
    .header .sub { margin: 0; font-size: 11px; opacity: 0.85; }
    .header .ver { font-size: 10px; opacity: 0.6; margin-top: 6px; }
    .body { padding: 28px 32px; }
    .section-title {
      font-size: 14px;
      font-weight: bold;
      color: ${AZUL};
      text-align: center;
      margin-bottom: 8px;
    }
    .divider { height: 2px; background: ${AZUL}; margin-bottom: 20px; }
    .box {
      border-top: 4px solid ${VERM};
      background: #f0f4ff;
      border: 1px solid #c7d2fe;
      padding: 20px 24px;
    }
    .box-title {
      font-size: 10px;
      font-weight: bold;
      color: ${AZUL};
      letter-spacing: 0.12em;
      text-transform: uppercase;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid #c7d2fe;
    }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px 24px;
      margin-bottom: 16px;
    }
    .field label {
      font-size: 9px;
      font-weight: bold;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      display: block;
      margin-bottom: 3px;
    }
    .field span { font-size: 12px; color: #374151; }
    .date-box {
      background: ${AZUL};
      color: white;
      padding: 10px 16px;
      text-align: center;
      font-size: 12px;
      font-weight: bold;
    }
    .footer {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 9px;
      color: #9ca3af;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>RP Métodos — Business Partners</h1>
    <p class="sub">Comprovante de Aceite dos Termos de Uso · RPLAY</p>
    <div class="ver">Versão dos Termos: ${versaoAceita}</div>
  </div>

  <div class="body">
    <div class="section-title">Registro de Aceite dos Termos de Uso</div>
    <div class="divider"></div>

    <div class="box">
      <div class="box-title">Dados do Colaborador</div>
      <div class="grid">
        <div class="field"><label>Nome</label><span>${user.name || '—'}</span></div>
        <div class="field"><label>E-mail</label><span>${user.email || '—'}</span></div>
        <div class="field"><label>Perfil de Acesso</label><span>${roleLabel}</span></div>
        <div class="field"><label>Versão Aceita</label><span>${versaoAceita}</span></div>
      </div>
      <div class="date-box">
        Data e hora do aceite: &nbsp;&nbsp;
        ${dataAceite ? formatDate(dataAceite) : '—'}
        &nbsp;&nbsp; (Horário de Brasília)
      </div>
    </div>

    <div class="footer">
      Documento gerado automaticamente pela plataforma RPLAY
      · RP Métodos Business Partners · Uso interno exclusivo
      · ${geradoEm}
    </div>
  </div>

  <script>
    window.onload = function () { window.print(); };
  ${'</script>'}
</body>
</html>`;

  const win = window.open('', '_blank', 'width=820,height=700');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
